import { DateRange, Matcher } from 'react-day-picker';

import { cn, NoddiIcon } from 'noddi-ui-common';
import { CustomDateFormats, NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { useId, useState } from 'react';
import { Button } from '../../../base/button/button';
import { Calendar } from '../../../base/calendar';
import { useIsWithinTailWindBreakpoint } from '../../../hooks/useIsWithinTailWindBreakpoint';
import { Popover, PopoverContent, PopoverTrigger } from '../NoddiPopover';
import { NoddiTooltip } from '../NoddiTooltip';
import { ClearButton } from './ClearButton';

export interface NoddiDateRangePickerProps {
  dateRange: DateRange | undefined;
  label?: string;
  /** Trigger text while nothing is picked. Name the date being filtered when the table has several. */
  placeholder?: string;
  id?: string;
  name?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  onDateChange: (date: DateRange | undefined) => void;
  showClearButton?: boolean;
  tooltip?: string;
  hidden?: Matcher | Matcher[] | undefined;
  disableHighlightToday?: boolean;
  className?: string;
  smallVersion?: boolean;
}

export function NoddiDateRangePicker({
  dateRange,
  onDateChange,
  showClearButton,
  label,
  placeholder = 'Pick a date range',
  id,
  name,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  tooltip,
  disableHighlightToday = false,
  hidden,
  className,
  smallVersion = false
}: NoddiDateRangePickerProps) {
  const handleClearDate = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDateChange(undefined);
  };

  const isLg = useIsWithinTailWindBreakpoint('lg');

  const dateFormat = smallVersion ? CustomDateFormats.DATE_SHORT_YEAR_NUMBERS_ONLY : NoddiDateFormats.DATE;
  const generatedId = useId();
  const controlId = id ?? name ?? generatedId;
  const labelIdentifier = `${controlId}-label`;
  const valueIdentifier = `${controlId}-value`;
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={cn('flex flex-col gap-0.5')}>
      {label && (
        <div className='flex items-center gap-2'>
          <label id={labelIdentifier} htmlFor={controlId} className='text-sm'>
            {label}
          </label>
          {tooltip && <NoddiTooltip content={tooltip} label={`More information about ${label}`} />}
        </div>
      )}
      {/* Anchor box for the clear button: mirrors the trigger's sizing classes so the
          absolutely positioned clear button tracks the trigger, not the stretched column. */}
      <div className={cn('relative', className)}>
        <Popover open={isOpen} onOpenChange={setIsOpen} modal>
          <PopoverTrigger asChild>
            <Button
              type='button'
              id={controlId}
              name={name}
              variant='outline'
              aria-labelledby={label ? `${labelIdentifier} ${valueIdentifier}` : valueIdentifier}
              aria-describedby={ariaDescribedBy}
              aria-invalid={ariaInvalid}
              aria-haspopup='dialog'
              aria-expanded={isOpen}
              className={cn(
                'relative h-9 w-full min-w-70 justify-start text-left text-base font-normal md:text-sm',
                !dateRange && 'text-muted-foreground',
                dateRange?.from && showClearButton && 'pr-12',
                className
              )}
            >
              <NoddiIcon name='Calendar' />
              <span id={valueIdentifier}>
                {dateRange?.from ? (
                  dateRange.to ? (
                    <>
                      {noddiFormatDate(dateRange.from, dateFormat)} - {noddiFormatDate(dateRange.to, dateFormat)}
                    </>
                  ) : (
                    noddiFormatDate(dateRange.from, dateFormat) + ' - '
                  )
                ) : (
                  placeholder
                )}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className='w-auto overflow-hidden p-0'>
            <Calendar
              hidden={hidden}
              disableHighlightToday={disableHighlightToday}
              // oxlint-disable-next-line jsx-a11y/no-autofocus -- calendar inside a popover the user just opened; focus has to move off the trigger for arrow-key date navigation
              autoFocus
              mode='range'
              weekStartsOn={1}
              defaultMonth={dateRange?.from}
              selected={dateRange}
              // No auto-close: react-day-picker reports {from, to: from} on the first click, so any
              // "close when complete" check fires immediately and makes picking a range impossible.
              onSelect={onDateChange}
              numberOfMonths={isLg ? 1 : 2}
              // its nice to hardcode the width, so it doesn't change when hiding days.
              className={cn(isLg ? 'w-62' : 'w-122')}
            />
          </PopoverContent>
        </Popover>
        {dateRange?.from && showClearButton && (
          <ClearButton handleClearDate={handleClearDate} label={label ? `Clear ${label}` : 'Clear date range'} />
        )}
      </div>
    </div>
  );
}
